<template>
    <ToolBarPanel
        :currentDep="currentDep"
        :listDep="listDep"
        @dep-changed="onDepChanged"
        :listApp="listApp"
        :currentApp="currentApp"
        @app-changed="onAppChanged"
        :fio="fio"
        :fioAsset="'ai-lk-svg-16x16'"
        :itemsCommand="itemsCommand"
        @user-command-click="onUserCommandClick"
        :period="period"
        :periodHint="'Текущий расчетный период'"
        :version="version"
        :versionHint="versionHint"
    />
</template>

<script>
export default {
    name: 'ToolBarPanelData',
};
</script>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { getUserApps, getUserDeparts } from '@/store/global';
import ToolBarPanel from '@/layouts/main-layout/components/top-menu/ToolBarPanel.vue';
import { urlInfo } from '@/utils/url-parse';
import { getOfficialUserData as getOfficialUserDetails, getUserByLogin } from '@/service/admin-service';
import { getBillingPeriod } from '@/service/meta-service';
import { deleteCookie, getCookie, setCookie } from '@/utils/cookies';
import { useRouter } from 'vue-router';
import enviromentStore from '@/store/enviroment-data';
import userLocalStore from '@/views/administration/users/store';
import { getOpenedTabs, resetTabState, saveTabContent } from '@/layouts/main-layout/components/navigation-bars-data';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';
import { setSaveUserDataPopupVisible } from '@/layouts/main-layout/main-layout-data';
import { processTab } from '@/layouts/main-layout/components/top-menu/utils';

const { state, MODE, setUserRequestBody } = userLocalStore;
const router = useRouter();
const listDep = ref([]);
const currentDep = ref({});
const listApp = ref([]);
const currentApp = ref({});
const fio = ref('');
const itemsCommand = ref([
    {
        name: 'command-settings',
        asset: 'ai-lk-svg-16x16',
        assetClass: 'ai-svg-color ai-svg-16',
        text: 'Настройки профиля',
    },
    {
        name: 'command-exit',
        asset: 'ai-top-menu-user-exit-svg',
        assetClass: 'ai-svg-color ai-svg-16',
        text: 'Выход',
    },
]);
const period = ref('');
const version = computed(() => process.env.PACKAGE_VERSION);
const versionHint = computed(() => {
    return `АСУСЭ - Автоматизированная Система Управления Сбытом Энергии
Версия ${process.env.PACKAGE_VERSION}
Год разработки - 2024г
Разработчик: ООО "АЛЬФА-ИНТЕГРАТОР-ИНФОЭНЕРГО"`;
});
onMounted(async () => {
    // Departs
    const userDeparts = await getUserDeparts();
    const resultDepList = getDepartmentList(userDeparts.items);
    listDep.value = resultDepList.items;
    currentDep.value = resultDepList.currentDep;

    // Apps
    const { result, items } = await getUserApps();
    if (!result) throw new Error('83ceafcb-90f4-4342-a4b8-51e91b086508');
    const resultAppList = getAppList(items);
    currentApp.value = resultAppList.currentApp;
    listApp.value = resultAppList.items;

    // User
    const user = await getUserByLogin();
    const official = await getOfficialUserDetails(user.official_id);
    fio.value = calcFIO(official);
    // period
    period.value = await getBillingPeriod({ n_podr: urlInfo().depId });
});

const dirtyTabsNotify = async (item, isLogout = false) => {
    const currentAppId = +getCookie('app');
    let resultDescription = 'dontSave';

    if (isLogout || currentAppId !== item?.id) {
        const dirtyTabs = getOpenedTabs().filter(tab => tab.isDirty);

        if (dirtyTabs.length) {
            const result = await AiCustomConfirmDialog(
                'В закрывающихся вкладках есть несохраненные изменения. Сохранить изменения?',
            );

            switch (result) {
                case -1:
                    resultDescription = 'cancel';
                    break;
                case 1:
                    resultDescription = 'save';
                    break;
            }
        }
    }
    return resultDescription;
};

const onDepChanged = async item => {
    const action = await dirtyTabsNotify(item);
    const currentLogin = getCookie('login');

    const changeDepartment = (item, currentLogin) => {
        setCookie('dep', item.id);
        const selectedDep = new Map();
        selectedDep.set(currentLogin, item.id);
        setCookie('selectedDep', JSON.stringify(Array.from(selectedDep.entries())));
        resetTabState();
        router.push({ name: 'AppDepartmentSelect' });
    };

    if (action === 'dontSave') {
        currentDep.value = item;
        changeDepartment(item, currentLogin);
    } else if (action === 'save') {
        let resultInsideCase;
        const dirtyTabs = getOpenedTabs().filter(tab => tab.isDirty);

        currentDep.value = item;
        setSaveUserDataPopupVisible(true);
        resultInsideCase = await processTab(dirtyTabs[0], 0, dirtyTabs, saveTabContent, true);
        setSaveUserDataPopupVisible(false);
        if (resultInsideCase) {
            changeDepartment(item, currentLogin);
        }
    }
};

const onAppChanged = async item => {
    const action = await dirtyTabsNotify(item);
    const currentLogin = getCookie('login');

    const changeApp = (selectedApp, currentLogin) => {
        setCookie('app', selectedApp.id);
        const selectedAppMap = new Map();
        selectedAppMap.set(currentLogin, selectedApp.id);
        setCookie('selectedApp', JSON.stringify(Array.from(selectedAppMap.entries())));
        router.push({ name: 'AppDepartmentSelect' });
    };

    if (action === 'dontSave') {
        currentApp.value = item;
        changeApp(item, currentLogin);
    } else if (action === 'save') {
        let resultInsideCase;
        const dirtyTabs = getOpenedTabs().filter(tab => tab.isDirty);

        currentApp.value = item;
        setSaveUserDataPopupVisible(true);
        resultInsideCase = await processTab(dirtyTabs[0], 0, dirtyTabs, saveTabContent, true);
        setSaveUserDataPopupVisible(false);
        if (resultInsideCase) {
            changeApp(item, currentLogin);
        }
    }
};

const logout = async () => {
    const action = await dirtyTabsNotify(null, true);

    if (action === 'save') {
        const dirtyTabs = getOpenedTabs().filter(tab => tab.isDirty);

        setSaveUserDataPopupVisible(true);
        await processTab(dirtyTabs[0], 0, dirtyTabs, saveTabContent, true);
        setSaveUserDataPopupVisible(false);
    } else if (action === 'cancel') {
        return;
    }

    deleteCookie('token');
    deleteCookie('renewal');
    deleteCookie('app');
    deleteCookie('dep');
    router.push({ name: 'loginForm' });
};

const openUserCard = async () => {
    state.stopWatching && state.stopWatching();
    state.isClosingConfirmed = true;
    state.requestBodyHasNotChanged = true;
    await enviromentStore.getOfficialUserData();
    await enviromentStore.getUserData();
    const user = {
        ...enviromentStore.state.officialData,
        ...enviromentStore.state.userData,
        id: enviromentStore.state.userData.id,
    };
    state.mode = MODE.VIEW;
    state.currentTabIndex = 0;
    state.deletedRoleKey = [];
    setUserRequestBody({ ...user });
    router.push({ name: 'profile-settings' });
};

const onUserCommandClick = item => {
    switch (item.name) {
        case 'command-settings':
            openUserCard();
            break;
        case 'command-exit':
            logout();
            break;
        default:
            break;
    }
};

const getDepartmentList = items => {
    const currentDepId = urlInfo().depId;
    const currentDep = items.find(dep => +currentDepId === dep.dep_id);
    const resultItems = items.map(dep => ({
        id: dep.dep_id,
        text: dep.name,
        asset: 'ai-dep-svg',
        assetClass: 'ai-svg-color ai-svg-16',
    }));
    if (currentDep) {
        return {
            currentDep: {
                id: currentDep.dep_id,
                text: currentDep.name,
                asset: 'ai-dep-svg',
                assetClass: 'ai-svg-color ai-svg-16',
            },
            items: resultItems,
        };
    }
    return { currentDep: {}, items: resultItems };
};

const getAppList = items => {
    const currentAppId = urlInfo().appId;
    const currentApp = items.find(app => +currentAppId === app.kod_app);

    const userApps = [];
    for (const app of items) {
        userApps.push({
            id: app.kod_app,
            text: app.name_app,
            asset: app.settings.app_svg,
            assetClass: 'ai-svg-color ai-svg-16',
        });
    }
    if (currentApp) {
        return {
            currentApp: {
                id: currentApp.kod_app,
                text: currentApp.name_app,
                asset: currentApp.settings.app_svg,
                assetClass: 'ai-svg-color ai-svg-16',
            },
            items: userApps,
        };
    }
    return {
        currentApp: {},
        items: userApps,
    };
};

const calcFIO = official => {
    if (official.fio) {
        const arrayFio = official.fio.split(' ');
        if (arrayFio.length === 3) {
            return arrayFio[0] + ' ' + arrayFio[1].charAt(0) + '. ' + arrayFio[2].charAt(0) + '.';
        } else if (arrayFio.length === 2) {
            return arrayFio[0] + ' ' + arrayFio[1].charAt(0) + '.';
        }
    }
    return '';
};
</script>
